<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import CortexTemplateInput from './CortexTemplateInput.vue';

const props = defineProps({
  tool: { type: Object, default: null },
  credentials: { type: Array, default: () => [] },
  contactCustomAttributes: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save', 'test']);
const { t } = useI18n();
const dialogRef = ref(null);
const jsonEditorDialogRef = ref(null);
const bodyMode = ref('fields');
const headerMode = ref('fields');
const queryMode = ref('fields');
const headerFields = ref([{ key: '', value: '' }]);
const queryFields = ref([{ key: '', value: '' }]);
const bodyFields = ref([{ key: '', value: '' }]);
const headerJson = ref('');
const queryJson = ref('');
const bodyJson = ref('');
const headerJsonError = ref('');
const queryJsonError = ref('');
const jsonError = ref('');
const expandedBodyJson = ref('');
const jsonEditorError = ref('');
const isJsonEditorOpen = ref(false);
const form = reactive({
  name: '',
  description: '',
  url: '',
  method: 'get',
  external_credential_id: '',
  risk_level: 'low',
  status: 'draft',
});

const methodOptions = computed(() =>
  ['get', 'post', 'put', 'patch', 'delete'].map(value => ({
    value,
    label: t(`CORTEX.TOOLS.FORM.METHODS.${value}`),
  }))
);
const riskOptions = computed(() =>
  ['low', 'medium', 'high', 'critical'].map(value => ({
    value,
    label: t(`CORTEX.TOOLS.RISK.${value}`),
  }))
);
const statusOptions = computed(() =>
  ['draft', 'active', 'disabled', 'archived'].map(value => ({
    value,
    label: t(`CORTEX.TOOLS.STATUS.${value}`),
  }))
);
const isEditMode = computed(() => Boolean(props.tool?.id));
const dialogTitle = computed(() =>
  t(
    isEditMode.value
      ? 'CORTEX.TOOLS.FORM.EDIT_DIALOG_TITLE'
      : 'CORTEX.TOOLS.FORM.DIALOG_TITLE'
  )
);
const credentialOptions = computed(() => [
  { value: '', label: t('CORTEX.TOOLS.FORM.NO_CREDENTIAL') },
  ...props.credentials.map(credential => ({
    value: credential.id,
    label: credential.name,
  })),
]);
const selectedCredentialFields = computed(() => {
  const credential = props.credentials.find(
    item => String(item.id) === String(form.external_credential_id)
  );
  return credential?.fields?.map(field => field.key) || [];
});
const methodSupportsBody = computed(() =>
  ['post', 'put', 'patch', 'delete'].includes(form.method)
);
const contactVariable = '{{contact.campo}}';
const credentialVariable = '{{credential.campo}}';
const credentialBaseUrlVariable = '{{credential.base_url}}';
const jsonBodyPlaceholder = JSON.stringify(
  { client: "{{contact.server_url.replace('https://', '')}}" },
  null,
  2
);
const jsonHeadersPlaceholder = JSON.stringify(
  { 'X-Account': '{{conversation.account_id}}' },
  null,
  2
);
const jsonQueryParamsPlaceholder = JSON.stringify(
  { email: '{{contact.email}}' },
  null,
  2
);

const addRequestField = fields => fields.push({ key: '', value: '' });
const removeRequestField = (fields, index) => fields.splice(index, 1);
const addBodyField = () => bodyFields.value.push({ key: '', value: '' });
const removeBodyField = index => bodyFields.value.splice(index, 1);
const fieldsToObject = fields =>
  fields.reduce((values, field) => {
    if (field.key.trim()) values[field.key.trim()] = field.value;
    return values;
  }, {});

const parsedJsonObject = (json, error, errorKey, objectErrorKey) => {
  error.value = '';
  if (!json.value.trim()) return {};

  try {
    const value = JSON.parse(json.value);
    if (Array.isArray(value) || typeof value !== 'object' || value === null) {
      error.value = t(objectErrorKey);
      return null;
    }
    return value;
  } catch {
    error.value = t(errorKey);
    return null;
  }
};

const parsedHeaders = () =>
  headerMode.value === 'json'
    ? parsedJsonObject(
        headerJson,
        headerJsonError,
        'CORTEX.TOOLS.FORM.HEADERS_JSON_ERROR',
        'CORTEX.TOOLS.FORM.HEADERS_JSON_OBJECT_ERROR'
      )
    : fieldsToObject(headerFields.value);

const parsedQueryParams = () =>
  queryMode.value === 'json'
    ? parsedJsonObject(
        queryJson,
        queryJsonError,
        'CORTEX.TOOLS.FORM.QUERY_PARAMS_JSON_ERROR',
        'CORTEX.TOOLS.FORM.QUERY_PARAMS_JSON_OBJECT_ERROR'
      )
    : fieldsToObject(queryFields.value);

const openJsonEditor = () => {
  expandedBodyJson.value = bodyJson.value;
  jsonEditorError.value = '';
  isJsonEditorOpen.value = true;
  nextTick(() => jsonEditorDialogRef.value?.open?.());
};

const closeJsonEditor = () => {
  isJsonEditorOpen.value = false;
  jsonEditorError.value = '';
};

const saveExpandedJson = () => {
  const content = expandedBodyJson.value.trim();
  if (content) {
    try {
      const value = JSON.parse(content);
      if (Array.isArray(value) || typeof value !== 'object' || value === null) {
        jsonEditorError.value = t('CORTEX.TOOLS.FORM.BODY_JSON_OBJECT_ERROR');
        return;
      }
    } catch {
      jsonEditorError.value = t('CORTEX.TOOLS.FORM.BODY_JSON_ERROR');
      return;
    }
  }

  bodyJson.value = expandedBodyJson.value;
  closeJsonEditor();
};

const parsedBody = () => {
  jsonError.value = '';
  if (!methodSupportsBody.value) return {};
  if (bodyMode.value === 'json') {
    if (!bodyJson.value.trim()) return {};
    try {
      const body = JSON.parse(bodyJson.value);
      if (Array.isArray(body) || typeof body !== 'object' || body === null) {
        jsonError.value = t('CORTEX.TOOLS.FORM.BODY_JSON_OBJECT_ERROR');
        return null;
      }
      return body;
    } catch {
      jsonError.value = t('CORTEX.TOOLS.FORM.BODY_JSON_ERROR');
      return null;
    }
  }

  return fieldsToObject(bodyFields.value);
};

const toolPayload = ({ headers, queryParams, body }) => ({
  name: form.name.trim(),
  description: form.description.trim(),
  tool_type: 'http_api',
  risk_level: form.risk_level,
  status: form.status,
  activation_confirmed: form.status === 'active',
  external_credential_id: form.external_credential_id || undefined,
  config: {
    url: form.url.trim(),
    method: form.method,
    headers,
    query_params: queryParams,
    body,
  },
  input_schema: {
    type: 'object',
    properties: { conversation_id: { type: 'integer' } },
    required: ['conversation_id'],
    additionalProperties: false,
  },
  output_schema: { type: 'object' },
});

const save = () => {
  const headers = parsedHeaders();
  const queryParams = parsedQueryParams();
  const body = parsedBody();
  if (
    !form.name.trim() ||
    !form.url.trim() ||
    headers === null ||
    queryParams === null ||
    body === null
  )
    return;

  emit('save', toolPayload({ headers, queryParams, body }));
};

const templateVariables = () => {
  const headers = parsedHeaders();
  const queryParams = parsedQueryParams();
  const body = parsedBody();
  if (
    !form.url.trim() ||
    headers === null ||
    queryParams === null ||
    body === null
  )
    return null;

  const expressions = JSON.stringify({
    url: form.url,
    headers,
    query_params: queryParams,
    body,
  }).matchAll(/\{\{\s*(contact|conversation|arguments)\.([a-zA-Z0-9_]+)/g);
  return [...expressions].reduce((variables, match) => {
    const [, namespace, key] = match;
    const id = `${namespace}.${key}`;
    if (!variables.some(variable => variable.id === id)) {
      variables.push({
        id,
        namespace,
        key,
        expression: `{{${id}}}`,
        label: t('CORTEX.TOOLS.FORM.TEST_VARIABLE_LABEL', { variable: id }),
      });
    }
    return variables;
  }, []);
};

const test = () => {
  const variables = templateVariables();
  if (!variables) return;
  const headers = parsedHeaders();
  const queryParams = parsedQueryParams();
  const body = parsedBody();
  if (headers === null || queryParams === null || body === null) return;

  emit('test', {
    tool: toolPayload({ headers, queryParams, body }),
    variables,
  });
};

const applyTool = tool => {
  form.name = tool?.name || '';
  form.description = tool?.description || '';
  form.url = tool?.config?.url || '';
  form.method = tool?.config?.method || 'get';
  form.external_credential_id = tool?.external_credential_id || '';
  form.risk_level = tool?.risk_level || 'low';
  form.status = tool?.status || 'draft';
  headerMode.value = 'fields';
  queryMode.value = 'fields';
  headerFields.value = Object.entries(tool?.config?.headers || {}).map(
    ([key, value]) => ({
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    })
  );
  if (!headerFields.value.length) headerFields.value = [{ key: '', value: '' }];
  headerJson.value = tool?.config?.headers
    ? JSON.stringify(tool.config.headers, null, 2)
    : '';
  headerJsonError.value = '';
  queryFields.value = Object.entries(tool?.config?.query_params || {}).map(
    ([key, value]) => ({
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    })
  );
  if (!queryFields.value.length) queryFields.value = [{ key: '', value: '' }];
  queryJson.value = tool?.config?.query_params
    ? JSON.stringify(tool.config.query_params, null, 2)
    : '';
  queryJsonError.value = '';
  const body = tool?.config?.body;
  bodyMode.value = 'fields';
  bodyFields.value = Object.entries(body || {}).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
  if (!bodyFields.value.length) bodyFields.value = [{ key: '', value: '' }];
  bodyJson.value = body ? JSON.stringify(body, null, 2) : '';
};

watch(() => props.tool, applyTool, { immediate: true });

nextTick(() => dialogRef.value?.open?.());
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="dialogTitle"
    :description="t('CORTEX.TOOLS.FORM.DIALOG_DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="emit('close')"
  >
    <div class="grid gap-5">
      <div class="grid gap-4 md:grid-cols-2">
        <WithLabel name="cortex-tool-name" :label="t('CORTEX.TOOLS.FORM.NAME')">
          <NextInput
            v-model="form.name"
            class="w-full"
            :placeholder="t('CORTEX.TOOLS.FORM.NAME_PLACEHOLDER')"
            required
          />
        </WithLabel>
        <WithLabel
          name="cortex-tool-description"
          :label="t('CORTEX.TOOLS.FORM.DESCRIPTION')"
        >
          <NextInput
            v-model="form.description"
            class="w-full"
            :placeholder="t('CORTEX.TOOLS.FORM.DESCRIPTION_PLACEHOLDER')"
          />
        </WithLabel>
      </div>
      <WithLabel
        name="cortex-tool-url"
        :label="t('CORTEX.TOOLS.FORM.URL')"
        :help-message="
          t('CORTEX.TOOLS.FORM.VARIABLES_HELP', {
            contactVariable,
            credentialVariable,
            credentialBaseUrlVariable,
          })
        "
      >
        <CortexTemplateInput
          v-model="form.url"
          class="w-full"
          :placeholder="t('CORTEX.TOOLS.FORM.URL_PLACEHOLDER')"
          :credential-fields="selectedCredentialFields"
          :contact-custom-attributes="contactCustomAttributes"
          required
        />
      </WithLabel>
      <div class="grid gap-4 md:grid-cols-2">
        <WithLabel
          name="cortex-tool-method"
          :label="t('CORTEX.TOOLS.FORM.METHOD')"
        >
          <AdaptiveSelect
            v-model="form.method"
            :options="methodOptions"
            :teleport="false"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          name="cortex-tool-external-credential"
          :label="t('CORTEX.TOOLS.FORM.EXTERNAL_CREDENTIAL')"
          :help-message="t('CORTEX.TOOLS.FORM.EXTERNAL_CREDENTIAL_HELP')"
        >
          <AdaptiveSelect
            v-model="form.external_credential_id"
            class="w-full"
            :options="credentialOptions"
            :teleport="false"
          />
        </WithLabel>
      </div>
      <section class="grid gap-3 border-t border-n-weak pt-5">
        <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
          <div class="min-w-0">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CORTEX.TOOLS.FORM.HEADERS') }}
            </h4>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('CORTEX.TOOLS.FORM.HEADERS_HELP') }}
            </p>
          </div>
          <div class="flex rounded-lg bg-n-alpha-2 p-1">
            <NextButton
              type="button"
              xs
              blue
              ghost
              icon="i-lucide-list"
              :class="{ 'bg-n-brand/10': headerMode === 'fields' }"
              :aria-label="t('CORTEX.TOOLS.FORM.HEADERS_FIELDS')"
              :title="t('CORTEX.TOOLS.FORM.HEADERS_FIELDS')"
              @click="headerMode = 'fields'"
            />
            <NextButton
              type="button"
              xs
              blue
              ghost
              icon="i-lucide-braces"
              :class="{ 'bg-n-brand/10': headerMode === 'json' }"
              :aria-label="t('CORTEX.TOOLS.FORM.HEADERS_JSON')"
              :title="t('CORTEX.TOOLS.FORM.HEADERS_JSON')"
              @click="headerMode = 'json'"
            />
          </div>
        </div>
        <div v-if="headerMode === 'fields'" class="grid gap-3">
          <div
            v-for="(field, index) in headerFields"
            :key="index"
            class="grid gap-2 sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)_auto]"
          >
            <NextInput
              v-model="field.key"
              :placeholder="t('CORTEX.TOOLS.FORM.HEADER_FIELD_NAME')"
            />
            <CortexTemplateInput
              v-model="field.value"
              :placeholder="t('CORTEX.TOOLS.FORM.HEADER_FIELD_VALUE')"
              :credential-fields="selectedCredentialFields"
              :contact-custom-attributes="contactCustomAttributes"
            />
            <NextButton
              type="button"
              sm
              ruby
              ghost
              icon="i-lucide-trash-2"
              :aria-label="t('CORTEX.TOOLS.FORM.REMOVE_HEADER_FIELD')"
              :title="t('CORTEX.TOOLS.FORM.REMOVE_HEADER_FIELD')"
              :disabled="headerFields.length === 1"
              @click="removeRequestField(headerFields, index)"
            />
          </div>
          <NextButton
            type="button"
            sm
            slate
            outline
            icon="i-lucide-plus"
            :label="t('CORTEX.TOOLS.FORM.ADD_HEADER_FIELD')"
            class="justify-self-start"
            @click="addRequestField(headerFields)"
          />
        </div>
        <div v-else>
          <CortexTemplateInput
            v-model="headerJson"
            multiline
            class="min-h-32 w-full font-mono"
            :placeholder="jsonHeadersPlaceholder"
            :credential-fields="selectedCredentialFields"
            :contact-custom-attributes="contactCustomAttributes"
          />
          <p v-if="headerJsonError" class="mt-1 text-sm text-n-ruby-11">
            {{ headerJsonError }}
          </p>
        </div>
      </section>
      <section class="grid gap-3 border-t border-n-weak pt-5">
        <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
          <div class="min-w-0">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CORTEX.TOOLS.FORM.QUERY_PARAMS') }}
            </h4>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('CORTEX.TOOLS.FORM.QUERY_PARAMS_HELP') }}
            </p>
          </div>
          <div class="flex rounded-lg bg-n-alpha-2 p-1">
            <NextButton
              type="button"
              xs
              blue
              ghost
              icon="i-lucide-list"
              :class="{ 'bg-n-brand/10': queryMode === 'fields' }"
              :aria-label="t('CORTEX.TOOLS.FORM.QUERY_PARAMS_FIELDS')"
              :title="t('CORTEX.TOOLS.FORM.QUERY_PARAMS_FIELDS')"
              @click="queryMode = 'fields'"
            />
            <NextButton
              type="button"
              xs
              blue
              ghost
              icon="i-lucide-braces"
              :class="{ 'bg-n-brand/10': queryMode === 'json' }"
              :aria-label="t('CORTEX.TOOLS.FORM.QUERY_PARAMS_JSON')"
              :title="t('CORTEX.TOOLS.FORM.QUERY_PARAMS_JSON')"
              @click="queryMode = 'json'"
            />
          </div>
        </div>
        <div v-if="queryMode === 'fields'" class="grid gap-3">
          <div
            v-for="(field, index) in queryFields"
            :key="index"
            class="grid gap-2 sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)_auto]"
          >
            <NextInput
              v-model="field.key"
              :placeholder="t('CORTEX.TOOLS.FORM.QUERY_PARAM_NAME')"
            />
            <CortexTemplateInput
              v-model="field.value"
              :placeholder="t('CORTEX.TOOLS.FORM.QUERY_PARAM_VALUE')"
              :credential-fields="selectedCredentialFields"
              :contact-custom-attributes="contactCustomAttributes"
            />
            <NextButton
              type="button"
              sm
              ruby
              ghost
              icon="i-lucide-trash-2"
              :aria-label="t('CORTEX.TOOLS.FORM.REMOVE_QUERY_PARAM')"
              :title="t('CORTEX.TOOLS.FORM.REMOVE_QUERY_PARAM')"
              :disabled="queryFields.length === 1"
              @click="removeRequestField(queryFields, index)"
            />
          </div>
          <NextButton
            type="button"
            sm
            slate
            outline
            icon="i-lucide-plus"
            :label="t('CORTEX.TOOLS.FORM.ADD_QUERY_PARAM')"
            class="justify-self-start"
            @click="addRequestField(queryFields)"
          />
        </div>
        <div v-else>
          <CortexTemplateInput
            v-model="queryJson"
            multiline
            class="min-h-32 w-full font-mono"
            :placeholder="jsonQueryParamsPlaceholder"
            :credential-fields="selectedCredentialFields"
            :contact-custom-attributes="contactCustomAttributes"
          />
          <p v-if="queryJsonError" class="mt-1 text-sm text-n-ruby-11">
            {{ queryJsonError }}
          </p>
        </div>
      </section>
      <section
        v-if="methodSupportsBody"
        class="grid gap-3 border-t border-n-weak pt-5"
      >
        <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
          <div class="min-w-0">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CORTEX.TOOLS.FORM.BODY') }}
            </h4>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('CORTEX.TOOLS.FORM.BODY_HELP') }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <div class="flex rounded-lg bg-n-alpha-2 p-1">
              <NextButton
                type="button"
                xs
                blue
                ghost
                icon="i-lucide-list"
                :class="{ 'bg-n-brand/10': bodyMode === 'fields' }"
                :aria-label="t('CORTEX.TOOLS.FORM.BODY_FIELDS')"
                :title="t('CORTEX.TOOLS.FORM.BODY_FIELDS')"
                @click="bodyMode = 'fields'"
              />
              <NextButton
                type="button"
                xs
                blue
                ghost
                icon="i-lucide-braces"
                :class="{ 'bg-n-brand/10': bodyMode === 'json' }"
                :aria-label="t('CORTEX.TOOLS.FORM.BODY_JSON')"
                :title="t('CORTEX.TOOLS.FORM.BODY_JSON')"
                @click="bodyMode = 'json'"
              />
            </div>
            <NextButton
              v-if="bodyMode === 'json'"
              type="button"
              sm
              slate
              outline
              icon="i-lucide-maximize"
              :aria-label="t('CORTEX.TOOLS.FORM.EXPAND_JSON')"
              :title="t('CORTEX.TOOLS.FORM.EXPAND_JSON')"
              @click="openJsonEditor"
            />
          </div>
        </div>
        <div v-if="bodyMode === 'fields'" class="grid gap-3">
          <div
            v-for="(field, index) in bodyFields"
            :key="index"
            class="grid gap-2 sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)_auto]"
          >
            <NextInput
              v-model="field.key"
              :placeholder="t('CORTEX.TOOLS.FORM.BODY_FIELD_NAME')"
            />
            <CortexTemplateInput
              v-model="field.value"
              :placeholder="t('CORTEX.TOOLS.FORM.BODY_FIELD_VALUE')"
              :credential-fields="selectedCredentialFields"
              :contact-custom-attributes="contactCustomAttributes"
            />
            <NextButton
              type="button"
              sm
              ruby
              ghost
              icon="i-lucide-trash-2"
              :aria-label="t('CORTEX.TOOLS.FORM.REMOVE_BODY_FIELD')"
              :title="t('CORTEX.TOOLS.FORM.REMOVE_BODY_FIELD')"
              :disabled="bodyFields.length === 1"
              @click="removeBodyField(index)"
            />
          </div>
          <NextButton
            type="button"
            sm
            slate
            outline
            icon="i-lucide-plus"
            :label="t('CORTEX.TOOLS.FORM.ADD_BODY_FIELD')"
            class="justify-self-start"
            @click="addBodyField"
          />
        </div>
        <div v-else>
          <CortexTemplateInput
            v-model="bodyJson"
            multiline
            class="min-h-40 w-full font-mono"
            :placeholder="jsonBodyPlaceholder"
            :credential-fields="selectedCredentialFields"
            :contact-custom-attributes="contactCustomAttributes"
          />
          <p v-if="jsonError" class="mt-1 text-sm text-n-ruby-11">
            {{ jsonError }}
          </p>
        </div>
      </section>
      <div class="grid gap-4 border-t border-n-weak pt-5 md:grid-cols-2">
        <WithLabel name="cortex-tool-risk" :label="t('CORTEX.TOOLS.FORM.RISK')">
          <AdaptiveSelect
            v-model="form.risk_level"
            :options="riskOptions"
            :teleport="false"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          name="cortex-tool-status"
          :label="t('CORTEX.TOOLS.FORM.STATUS')"
          :help-message="t('CORTEX.TOOLS.FORM.STATUS_HELP')"
        >
          <AdaptiveSelect
            v-model="form.status"
            :options="statusOptions"
            :teleport="false"
            class="w-full"
          />
        </WithLabel>
      </div>
    </div>
    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <NextButton
          type="button"
          slate
          faded
          icon="i-lucide-x"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="dialogRef?.close?.()"
        />
        <div class="flex flex-wrap items-center gap-3">
          <NextButton
            type="button"
            slate
            outline
            icon="i-lucide-play"
            :label="t('CORTEX.TOOLS.FORM.TEST')"
            :disabled="isLoading"
            @click="test"
          />
          <NextButton
            type="button"
            blue
            icon="i-lucide-save"
            :label="
              t(
                isEditMode
                  ? 'CORTEX.TOOLS.FORM.UPDATE'
                  : 'CORTEX.TOOLS.FORM.SUBMIT'
              )
            "
            :is-loading="isLoading"
            @click="save"
          />
        </div>
      </div>
    </template>
  </Dialog>
  <Dialog
    v-if="isJsonEditorOpen"
    ref="jsonEditorDialogRef"
    width="6xl"
    overflow-y-auto
    :title="t('CORTEX.TOOLS.FORM.JSON_EDITOR_TITLE')"
    :description="t('CORTEX.TOOLS.FORM.JSON_EDITOR_DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="closeJsonEditor"
  >
    <CortexTemplateInput
      v-model="expandedBodyJson"
      multiline
      class="min-h-[60vh] w-full font-mono"
      :placeholder="jsonBodyPlaceholder"
      :credential-fields="selectedCredentialFields"
      :contact-custom-attributes="contactCustomAttributes"
    />
    <p v-if="jsonEditorError" class="mt-1 text-sm text-n-ruby-11">
      {{ jsonEditorError }}
    </p>
    <template #footer>
      <div class="flex flex-wrap items-center justify-end gap-3">
        <NextButton
          type="button"
          slate
          faded
          icon="i-lucide-x"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="closeJsonEditor"
        />
        <NextButton
          type="button"
          blue
          icon="i-lucide-check"
          :label="t('CORTEX.TOOLS.FORM.APPLY_JSON')"
          @click="saveExpandedJson"
        />
      </div>
    </template>
  </Dialog>
</template>
